import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';

type ErrorDomainKeys =
  | 'inventory'
  | 'order'
  | 'comment'
  | 'line-item'
  | 'support-case'
  | 'dashboard'
  | 'grid'
  | 'general'
  | 'portal-user'
  | 'account-customization'
  | 'role'
  | 'product'
  | 'account'
  | 'user'
  | 'session'
  | 'supplier'
  | 'configuration'
  | 'attachments'
  | 's3-attachments'
  | 'outside-inventory'
  | 'outside-inventory-schema'
  | 'notifications'
  | 'teams'
  | 'my-team-page'
  | 'project'
  | 'project-contact'
  | 'shared-account-customization'
  | 'tag'
  | 'benchmark'
  | 'service-request'
  | 'schedule-settings'
  | 'advise-customer'
  | 'advise-engagement'
  | 'advise-supplier'
  | 'advise-task'
  | 'workflow-orders'
  | 'reports'
  | 'locations';
type ErrorsByDomain = Record<ErrorDomainKeys, string | undefined>;

export interface UseErrorStoreReturn {
  errorMessageSubscription: typeof userErrorMessageSubject;
  errorsByDomain: ErrorsByDomain;
}

const defaultErrorByDomain: ErrorsByDomain = {
  inventory: undefined,
  order: undefined,
  comment: undefined,
  'line-item': undefined,
  'support-case': undefined,
  dashboard: undefined,
  grid: undefined,
  general: undefined,
  'portal-user': undefined,
  'account-customization': undefined,
  role: undefined,
  product: undefined,
  account: undefined,
  user: undefined,
  session: undefined,
  supplier: undefined,
  configuration: undefined,
  attachments: undefined,
  's3-attachments': undefined,
  'outside-inventory': undefined,
  'outside-inventory-schema': undefined,
  notifications: undefined,
  teams: undefined,
  'my-team-page': undefined,
  project: undefined,
  'project-contact': undefined,
  'shared-account-customization': undefined,
  tag: undefined,
  benchmark: undefined,
  'service-request': undefined,
  'schedule-settings': undefined,
  'advise-customer': undefined,
  'advise-engagement': undefined,
  'advise-supplier': undefined,
  'advise-task': undefined,
  'workflow-orders': undefined,
  reports: undefined,
  locations: undefined
};

const defaultUserErrorMessage = undefined;

const errorByDomainSubject = new BehaviorSubject<ErrorsByDomain>(defaultErrorByDomain);
const userErrorMessageSubject = new BehaviorSubject<string | undefined>(defaultUserErrorMessage);

const updateErrorMessage = (message: string): void => userErrorMessageSubject.next(message);
export const getErrorMessageSubject = () => userErrorMessageSubject;
export const getErrorByDomainSubject = () => errorByDomainSubject;
export const addError = (domain: ErrorDomainKeys, message: string | undefined): void => {
  // Re-emitting an unchanged message re-renders every subscriber, which lets a retrying caller drive an update loop.
  if (errorByDomainSubject.value[domain] === message) return;

  errorByDomainSubject.next({ ...errorByDomainSubject.value, [domain]: message });
};
const hasAnyError = (errors: ErrorsByDomain): boolean => Object.values(errors).some((message) => !!message);

export const resetErrorStore = (): void => {
  // Subscribers call this from inside an emission, so re-entering the subjects with unchanged values must be avoided.
  // Clearing a domain leaves an object that is already empty but no longer the default one, so compare by content.
  if (hasAnyError(errorByDomainSubject.value)) errorByDomainSubject.next(defaultErrorByDomain);
  if (userErrorMessageSubject.value !== defaultUserErrorMessage) userErrorMessageSubject.next(defaultUserErrorMessage);
};

export const useErrorStore = (): UseErrorStoreReturn => {
  const [errorsByDomain, setErrorsByDomain] = useState<ErrorsByDomain>(defaultErrorByDomain);

  const handleErrorsByDomainUpdate = (): void => {
    const errorsArray = Object.keys(errorsByDomain).reduce((msgs: string[], key: string): string[] => {
      const currError = errorsByDomain[key as unknown as ErrorDomainKeys];
      if (currError && !msgs.includes(currError)) msgs.push(currError);

      return msgs;
    }, []);
    const errors = errorsArray.join(', ');

    if (errors) updateErrorMessage(errors);
  };

  useEffect(() => {
    const domainErrorSub = errorByDomainSubject.subscribe((errors) => {
      setErrorsByDomain(errors);
    });

    return () => {
      if (domainErrorSub) domainErrorSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    handleErrorsByDomainUpdate();
  }, [JSON.stringify(errorsByDomain)]);

  return { errorMessageSubscription: userErrorMessageSubject, errorsByDomain };
};
